import { useEffect } from "react";

const TASKMASTER_KEY = "taskMaster";
const TASKMASTER_VERSION = 1;
const HEARTBEAT_MS = 30000;

function browserReady() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function monotonicNowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }

  return Date.now();
}

function isoNow() {
  return new Date().toISOString();
}

function secondsSince(startMs, endMs) {
  const seconds = Math.max(0, (endMs - startMs) / 1000);
  return Math.round(seconds * 1000) / 1000;
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function cleanIntervals(values, predicate) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map(finiteNumber)
    .filter((value) => value !== null && value !== 0 && predicate(value));
}

function totalOnTask(intervals) {
  return intervals
    .filter((value) => value > 0)
    .reduce((sum, value) => sum + value, 0);
}

function totalOffTask(intervals) {
  return Math.abs(
    intervals
      .filter((value) => value < 0)
      .reduce((sum, value) => sum + value, 0)
  );
}

function roundedSeconds(value) {
  return Math.round(value * 1000) / 1000;
}

function withDerivedTotals(record) {
  const total = record.totalOnTask + record.totalOffTask;

  return {
    ...record,
    totalOnTask: roundedSeconds(record.totalOnTask),
    totalOffTask: roundedSeconds(record.totalOffTask),
    offTaskRatio: total > 0 ? roundedSeconds(record.totalOffTask / total) : 0,
    intervals: [...record.intervals],
    onTask: [...record.onTask],
    offTask: [...record.offTask],
  };
}

function focusedWindow() {
  if (!browserReady()) {
    return true;
  }

  if (document.visibilityState === "hidden") {
    return false;
  }

  if (typeof document.hasFocus === "function") {
    return document.hasFocus();
  }

  return true;
}

function stageValue(stageNode, key) {
  try {
    return stageNode?.get?.(key);
  } catch {
    return null;
  }
}

function setStageValue(stageNode, key, value) {
  try {
    stageNode?.set?.(key, value);
  } catch {
    // Telemetry should never interrupt the experiment flow.
  }
}

function initialRecord(existing, context, isFocused) {
  const matchingExisting =
    existing &&
    typeof existing === "object" &&
    existing.stageId === context.stageId;
  const source = matchingExisting ? existing : {};
  const intervals = cleanIntervals(source.intervals, () => true);
  const onTask = cleanIntervals(source.onTask, (value) => value > 0);
  const offTask = cleanIntervals(source.offTask, (value) => value < 0);
  const normalizedOnTask = onTask.length > 0 ? onTask : intervals.filter((value) => value > 0);
  const normalizedOffTask = offTask.length > 0 ? offTask : intervals.filter((value) => value < 0);
  const normalizedIntervals =
    intervals.length > 0 ? intervals : [...normalizedOnTask, ...normalizedOffTask];
  const storedFocusLossCount = finiteNumber(source.focusLossCount);

  return {
    version: TASKMASTER_VERSION,
    gameId: context.gameId,
    roundId: context.roundId,
    stageId: context.stageId,
    task: context.task,
    stageName: context.stageName,
    playerId: context.playerId,
    startedAt: typeof source.startedAt === "string" ? source.startedAt : isoNow(),
    updatedAt: isoNow(),
    finalizedAt: null,
    intervals: normalizedIntervals,
    onTask: normalizedOnTask,
    offTask: normalizedOffTask,
    totalOnTask: roundedSeconds(totalOnTask(normalizedIntervals)),
    totalOffTask: roundedSeconds(totalOffTask(normalizedIntervals)),
    focusLossCount:
      storedFocusLossCount !== null
        ? Math.max(0, Math.round(storedFocusLossCount))
        : normalizedOffTask.length,
    isFocused,
    lastEvent: "start",
  };
}

function appendClosedInterval(record, wasFocused, durationSeconds) {
  if (durationSeconds <= 0) {
    return;
  }

  if (wasFocused) {
    record.intervals.push(durationSeconds);
    record.onTask.push(durationSeconds);
    record.totalOnTask = roundedSeconds(record.totalOnTask + durationSeconds);
    return;
  }

  const offTaskDuration = -durationSeconds;
  record.intervals.push(offTaskDuration);
  record.offTask.push(offTaskDuration);
  record.totalOffTask = roundedSeconds(record.totalOffTask + durationSeconds);
}

function snapshotWithOpenInterval(record, isFocused, intervalStartedAtMs, reason) {
  const snapshot = {
    ...record,
    intervals: [...record.intervals],
    onTask: [...record.onTask],
    offTask: [...record.offTask],
    updatedAt: isoNow(),
    isFocused,
    lastEvent: reason,
  };

  appendClosedInterval(
    snapshot,
    isFocused,
    secondsSince(intervalStartedAtMs, monotonicNowMs())
  );

  return withDerivedTotals(snapshot);
}

export function useTaskMaster({ enabled = true, game, player, round, stage }) {
  const gameId = game?.id || "";
  const playerId = player?.id || "";
  const roundId = round?.id || "";
  const stageId = stage?.id || "";
  const task = typeof round?.get === "function" ? round.get("task") || "" : "";
  const stageName = typeof stage?.get === "function" ? stage.get("name") || "" : "";

  useEffect(() => {
    if (!enabled || !browserReady() || !playerId || !roundId || !stageId) {
      return undefined;
    }

    const stageNode = player?.stage;
    if (!stageNode) {
      return undefined;
    }

    let active = true;
    let isFocused = focusedWindow();
    let intervalStartedAtMs = monotonicNowMs();
    const context = { gameId, playerId, roundId, stageId, task, stageName };
    const record = initialRecord(
      stageValue(stageNode, TASKMASTER_KEY),
      context,
      isFocused
    );

    function persist(value) {
      setStageValue(stageNode, TASKMASTER_KEY, withDerivedTotals(value));
    }

    function closeCurrentInterval(nowMs) {
      appendClosedInterval(
        record,
        isFocused,
        secondsSince(intervalStartedAtMs, nowMs)
      );
      intervalStartedAtMs = nowMs;
    }

    function transitionTo(nextFocused, reason) {
      if (!active || nextFocused === isFocused) {
        return;
      }

      const nowMs = monotonicNowMs();
      closeCurrentInterval(nowMs);

      if (isFocused && !nextFocused) {
        record.focusLossCount += 1;
      }

      isFocused = nextFocused;
      record.isFocused = nextFocused;
      record.updatedAt = isoNow();
      record.lastEvent = reason;
      persist(record);
    }

    function handleFocus() {
      transitionTo(focusedWindow(), "focus");
    }

    function handleBlur() {
      transitionTo(false, "blur");
    }

    function handleVisibilityChange() {
      transitionTo(focusedWindow(), "visibilitychange");
    }

    function handlePageHide() {
      finalize("pagehide");
    }

    function finalize(reason) {
      if (!active) {
        return;
      }

      active = false;
      closeCurrentInterval(monotonicNowMs());
      record.isFocused = isFocused;
      record.updatedAt = isoNow();
      record.finalizedAt = record.updatedAt;
      record.lastEvent = reason;
      persist(record);
    }

    persist(record);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const heartbeatId = window.setInterval(() => {
      if (!active) {
        return;
      }

      setStageValue(
        stageNode,
        TASKMASTER_KEY,
        snapshotWithOpenInterval(
          record,
          isFocused,
          intervalStartedAtMs,
          "heartbeat"
        )
      );
    }, HEARTBEAT_MS);

    return () => {
      window.clearInterval(heartbeatId);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      finalize("cleanup");
    };
  }, [enabled, gameId, playerId, roundId, stageId, task, stageName]);
}
